import { Language } from '@net0-calc/common/types';
import { getSupabase } from '@net0-calc/common/services';
import { CalculatorFormValues } from 'common/types';
import { config } from 'config';

export type Answer = {
  id: string;
  answers: {
    formValues: CalculatorFormValues;
    co2Footprint: {
      cateringKg: number;
      travelAndAccommodationKg: number;
      merchandiseKg: number;
      eventDurationAndInfoKg: number;
      hospitalityKg: number;
    };
  };
};

export const api = {
  async sendAnswers({ answers }: { answers: unknown }) {
    // Use Edge Function to capture IP address and store permanently
    const response = await fetch(`${config.SUPABASE_URL}/functions/v1/save-answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        calculator_id: config.SUPABASE_CALCULATOR_ID,
        answers: answers as Record<string, unknown>,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save answers');
    }

    const result = await response.json();
    if (!result.id) throw new Error('No data returned');
    return { data: result.id };
  },

  async sendEmail({ answerId, email, localization }: { answerId: string; email: string; localization?: string }) {
    const response = await fetch(`${config.SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        answerId,
        email,
        localization: localization || Language.ENGLISH,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send email');
    }

    return { data: true };
  },

  async getAnswerById(answerId: string) {
    const supabase = getSupabase();

    const { data, error } = await supabase.from('calculator_answers').select('*').eq('id', answerId).single();

    if (error) throw error;

    const result = data as { id: string; answers: unknown } | null;
    if (!result) throw new Error('Answer not found');

    return {
      data: {
        id: result.id,
        answers: result.answers as Answer['answers'],
      } as Answer,
    };
  },

  async getResultImage(answerId: string, html: string) {
    const response = await fetch(`${config.SUPABASE_URL}/functions/v1/generate-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        answerId,
        html,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to generate image');
    }

    const result = await response.json();
    return { data: result.shareUrl || '' };
  },
};
